import React, { useEffect, useState } from "react";
import dummyImg from "../../assets/images/dummy.png";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import DummyAvatar from "../../assets/images/Profile-thumbnail-small.svg";
import { Image } from "react-bootstrap";
import PhoneNumberFormat from "../PhoneNumberFormate/PhoneNumberFormate";
import { format } from "date-fns";

function CourseDetailModal({ course, onClose }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (course) {
      setIsVisible(true);
    }
  }, [course]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(), 200);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!course) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      created_pending: { label: "Pending", class: "status-pending" },
      approved: { label: "Approved", class: "status-approved" },
      rejected: { label: "Rejected", class: "status-rejected" },
      active: { label: "Active", class: "status-active" },
    };

    const config = statusConfig[status] || {
      label: status,
      class: "status-default",
    };

    return (
      <span className={`status-badge ${config.class}`}>{config.label}</span>
    );
  };

  return (
    <>
      <style jsx>{`
        .detail-grid {
          display: grid;
          gap: 20px;
        }

        .detail-item {
          background: #f8fafc;
          border-radius: 12px;
          padding: 16px;
          border-left: 4px solid #f05203;
        }

        .detail-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #6b7280;
          margin-bottom: 4px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .detail-value {
          font-size: 1rem;
          font-weight: 500;
          color: #1f2937;
          line-height: 1.5;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 0.875rem;
          font-weight: 500;
          text-transform: capitalize;
        }

        .status-pending {
          background: #fef3c7;
          color: #d97706;
        }

        .status-approved {
          background: #d1fae5;
          color: #065f46;
        }

        .status-rejected {
          background: #fee2e2;
          color: #dc2626;
        }

        .status-active {
          background: #dbeafe;
          color: #1d4ed8;
        }

        .status-default {
          background: #f3f4f6;
          color: #6b7280;
        }
      `}</style>
      <div
        className="modal show"
        style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title fw-bold">Course Details</h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-label">Course Title</div>
                  <div className="detail-value">{course.title}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Description</div>
                  <div className="detail-value description-text">
                    {course.description || "No description available"}
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Category</div>
                  <div className="detail-value">
                    {course.Category?.name || "Uncategorized"}
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Status</div>
                  <div className="detail-value">
                    {getStatusBadge(course.status)}
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Created Date</div>
                  <div className="detail-value">
                    {formatDate(course.created_at)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function RequestCoursesTable({
  fetchApi,
  linkTo,
  isCourseTable,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const auth = useSelector((state) => state.auth);
  const [allData, setAllData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!auth.token) {
        setError(
          isCourseTable
            ? "Please log in to view courses."
            : "Please log in to view students."
        );
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetchApi(auth.token);
        if (response?.success) {
          if (isCourseTable) {
            setAllData(response.data || []);
          } else {
            setAllData(response.data.details || []);
          }
        } else {
          throw new Error(response?.message || "Failed to fetch data");
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
        toast.error(
          error?.message || "Failed to fetch data. Please try again.",
          {
            position: "top-center",
          }
        );
        setError(error?.message || "Failed to fetch data");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [auth.token, fetchApi, isCourseTable]);

  const totalPages = Math.ceil((allData?.length || 0) / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentRows = Array.isArray(allData)
    ? allData.slice(startIndex, endIndex)
    : [];

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleRowsChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      created_pending: { label: "Pending", class: "status-pending" },
      approved: { label: "Approved", class: "status-approved" },
      rejected: { label: "Rejected", class: "status-rejected" },
      active: { label: "Active", class: "status-active" },
    };

    const config = statusConfig[status] || {
      label: status,
      class: "status-default",
    };

    return (
      <span className={`status-badge rounded-3 ${config.class}`}>{config.label}</span>
    );
  };

  const isValidDate = (date) => {
    return date && !isNaN(new Date(date).getTime());
  };
  // Table for pending approval courses
  if (isCourseTable) {
    return (
      <>
        {isLoading && (
          <div className="loader-container">
            <div className="dottedLoading theme-color"></div>
          </div>
        )}
        <style jsx>{`
          .status-badge {
            display: inline-flex;
            align-items: center;
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 0.875rem;
            font-weight: 500;
            text-transform: capitalize;
          }

          .status-pending {
            background: #82BD001F;
            color: #82BD00;
          }

          .status-approved {
            background: #d1fae5;
            color: #065f46;
          }

          .status-rejected {
            background: #fee2e2;
            color: #dc2626;
          }

          .status-active {
            background: #dbeafe;
            color: #1d4ed8;
          }

          .status-default {
            background: #f3f4f6;
            color: #6b7280;
          }
        `}</style>
        <CourseDetailModal
          course={selectedCourse}
          onClose={() => setSelectedCourse(null)}
        />
        <div className="d-flex align-item-center justify-content-end d-sm-none">
          <label className="me-2 flex-shrink-0 align-self-center">
            No of Rows
          </label>
          <select
            className="form-control ms-16"
            value={rowsPerPage}
            onChange={handleRowsChange}
            style={{ width: "100px" }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={30}>30</option>
            <option value={40}>40</option>
            <option value={50}>50</option>
          </select>
        </div>
        <div className="table-responsive mb-10">
          <table className="table student-table custom-table align-middle">
            <thead className="position-sticky top-0">
              <tr>
                <th className="ah-text-base" width="10%">
                  Sr No.
                </th>
                <th className="ah-text-base" width="25%">
                  Course Name
                </th>
                <th className="ah-text-base" width="20%">
                  Category
                </th>
                <th className="ah-text-base" width="20%">
                  Created At
                </th>
                <th className="ah-text-base" width="15%">
                  Status
                </th>
                <th className="ah-text-base" width="10%">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="ah-text-sm fw-medium text-black">
              {isLoading ? (
                <tr>
                  <td colSpan="6">Loading...</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="6">
                    <div className="alert alert-danger">Error: {error}</div>
                  </td>
                </tr>
              ) : currentRows.length > 0 ? (
                currentRows.map((course, index) => (
                  <tr key={course.id}>
                    <td>
                      <div className="counter-block">
                        {startIndex + index + 1}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="img-holder me-15 flex-shrink-0">
                          <Image
                            src={
                              process.env.REACT_APP_GALLERY_URL +
                              JSON.parse(course?.image_urls)?.[0]
                            }
                            alt={`Thumbnail ${index + 1}`}
                            className="img-fluid"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = DummyAvatar;
                            }}
                          />
                        </div>
                        <strong className="ah-text-sm fw-semibold text-black">
                          {course.title}
                        </strong>
                      </div>
                    </td>
                    <td>{course.Category?.name || "-"}</td>
                    <td>
                      {isValidDate(course.created_at)
                        ? format(new Date(course.created_at), "MM-dd-yyyy")
                        : "-"}
                    </td>
                    <td>{getStatusBadge(course.status)}</td>
                    <td>
                      <Link
                        className="btn btn-primary fw-semibold no-shadow py-7 ah-text-sm"
                        // onClick={() => setSelectedCourse(course)}
                        to={"/instructor-course-detail?id=" + course?.id}
                      >
                        View Details
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6">
                    <div className="alert alert-info fw-normal">
                      No courses found.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="d-flex justify-content-center justify-content-sm-between align-items-center custom-pagination-holder">
          <div className="mx-auto">
            <button
              className="me-2 border-0 bg-transparent p-0"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <svg
                style={{ transform: "rotate(-90deg)" }}
                width="12"
                height="10"
                viewBox="0 0 17 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.7646 8.65686L8.76465 1.65686L1.76465 8.65686"
                  stroke="#000"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Prev
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i + 1}
                className={`bg-transparent border-0 ${
                  currentPage === i + 1
                    ? "pagination-item active"
                    : "pagination-item"
                } me-2`}
                onClick={() => handlePageChange(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button
              className="me-2 border-0 bg-transparent p-0"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
              <svg
                style={{ transform: "rotate(90deg)" }}
                width="12"
                height="10"
                viewBox="0 0 17 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.7646 8.65686L8.76465 1.65686L1.76465 8.65686"
                  stroke="#000"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
          <div className="align-item-center d-none d-sm-flex">
            <label className="me-2 flex-shrink-0 align-self-center">
              No of Rows
            </label>
            <select
              className="form-control ms-16"
              value={rowsPerPage}
              onChange={handleRowsChange}
              style={{ width: "100px" }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
              <option value={40}>40</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      </>
    );
  }

  // Default: Student Table
  return (
    <>
      <div className="d-flex align-item-center justify-content-end d-sm-none">
        <label className="me-2 flex-shrink-0 align-self-center">
          No of Rows
        </label>
        <select
          className="form-control ms-16"
          value={rowsPerPage}
          onChange={handleRowsChange}
          style={{ width: "100px" }}
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={30}>30</option>
          <option value={40}>40</option>
          <option value={50}>50</option>
        </select>
      </div>
      <div className="table-responsive mb-10">
        <table className="table student-table custom-table align-middle">
          <thead className="position-sticky top-0">
            <tr>
              <th className="ah-text-base" width="10%">
                Sr No.
              </th>
              <th className="ah-text-base" width="20%">
                Name
              </th>
              <th className="ah-text-base" width="20%">
                Email
              </th>
              <th className="ah-text-base" width="20%">
                Phone Number
              </th>
              <th className="ah-text-base" width="20%">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="5">Loading...</td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="5">Error: {error}</td>
              </tr>
            ) : currentRows.length > 0 ? (
              currentRows.map((student, index) => (
                <tr key={student.id}>
                  <td>
                    <div className="counter-block">
                      {startIndex + index + 1}
                    </div>
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      <div className="img-holder me-15">
                        <img
                          src={
                            student.profile_picture &&
                            student.profile_picture !== ""
                              ? student.profile_picture
                              : dummyImg
                          }
                          alt="Student"
                          className="img-fluid"
                          onError={(e) => (e.target.src = dummyImg)}
                        />
                      </div>
                      <strong className="ah-text-sm fw-semibold">
                        {student.full_name}
                      </strong>
                    </div>
                  </td>
                  <td className="fw-semibold">{student.email}</td>
                  <td className="fw-semibold">
                    <PhoneNumberFormat
                      number={student.phone_number}
                      type="national"
                    />
                  </td>
                  <td className="fw-semibold">
                    <Link
                      to={`${linkTo}?id=${student.id}`}
                      className="btn btn-primary fw-semibold no-shadow py-7 ah-text-sm"
                    >
                      View Details
                    </Link>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5">No students found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="d-flex justify-content-center justify-content-sm-between align-items-center custom-pagination-holder">
        <div className="mx-auto">
          <button
            className="me-2 border-0 bg-transparent p-0"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <svg
              style={{ transform: "rotate(-90deg)" }}
              width="12"
              height="10"
              viewBox="0 0 17 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.7646 8.65686L8.76465 1.65686L1.76465 8.65686"
                stroke="#000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Prev
          </button>
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i + 1}
              className={`bg-transparent border-0 ${
                currentPage === i + 1
                  ? "pagination-item active"
                  : "pagination-item"
              } me-2`}
              onClick={() => handlePageChange(i + 1)}
            >
              {i + 1}
            </button>
          ))}
          <button
            className="me-2 border-0 bg-transparent p-0"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
            <svg
              style={{ transform: "rotate(90deg)" }}
              width="12"
              height="10"
              viewBox="0 0 17 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.7646 8.65686L8.76465 1.65686L1.76465 8.65686"
                stroke="#000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
        <div className="align-item-center d-none d-sm-flex">
          <label className="me-2 flex-shrink-0 align-self-center">
            No of Rows
          </label>
          <select
            className="form-control ms-16"
            value={rowsPerPage}
            onChange={handleRowsChange}
            style={{ width: "100px" }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={30}>30</option>
            <option value={40}>40</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>
    </>
  );
}
